<template>
  <footer class="site-footer">
    <!-- 背景装饰 -->
    <div class="background-decorations">
      <div class="mountain-silhouette"></div>
      <div class="decorative-circle red-blur"></div>
      <div class="decorative-circle beige-blur"></div>
    </div>
    
    <div class="footer-container">
      <!-- 顶部装饰 -->
      <div class="top-decoration"></div>
      
      <!-- 主要内容 -->
      <div class="content-grid">
        <!-- 品牌区域 -->
        <div class="brand-section animated-border">
          <div class="brand-header">
            <div class="logo">
              <span class="logo-character">晋</span>
            </div>
            <h3 class="brand-title">山西旅游</h3>
          </div>
          
          <p class="brand-description">
            探索黄土高原的历史文化瑰宝，感受三晋大地的千年文明。山西，一个让时光慢下来的地方。
          </p>
          
          <!-- 社交媒体链接 -->
          <div class="social-links">
            <a 
              v-for="(icon, index) in socialIcons" 
              :key="index"
              href="#" 
              class="social-link"
              aria-label="社交媒体"
            >
              <component :is="icon" class="social-icon" />
            </a>
          </div>
          
          <div class="decorative-line vertical"></div>
        </div>
        
        <!-- 热门景点 -->
        <div class="section animated-border">
          <h4 class="section-title">
            热门景点
            <span class="title-underline"></span>
          </h4>
          
          <ul class="attraction-list">
            <li 
              v-for="attraction in popularAttractions" 
              :key="attraction.id" 
              class="attraction-item"
            >
              <router-link 
                :to="`/attractions/${attraction.id}`" 
                class="attraction-link"
              >
                <span class="bullet"></span>
                {{ attraction.name }}
              </router-link>
            </li>
          </ul>
          
          <div class="decorative-line vertical"></div>
        </div>
        
        <!-- 实用信息 -->
        <div class="section animated-border">
          <h4 class="section-title">
            实用信息
            <span class="title-underline"></span>
          </h4>
          
          <ul class="useful-links">
            <li 
              v-for="(link, index) in usefulLinks" 
              :key="index" 
              class="link-item"
            >
              <router-link 
                :to="link.path" 
                class="link"
              >
                <span class="bullet"></span>
                {{ link.label }}
              </router-link>
            </li>
          </ul>
          
          <div class="decorative-line vertical"></div>
        </div>
        
        <!-- 联系我们 -->
        <div class="contact-section animated-border">
          <h4 class="section-title">
            联系我们
            <span class="title-underline"></span>
          </h4>
          
          <div class="contact-info">
            <div class="contact-item">
              <div class="icon-container">
                <MapPinIcon class="contact-icon" />
              </div>
              <span class="contact-text">山西省太原市迎泽区迎泽大街79号</span>
            </div>
            
            <div class="contact-item">
              <div class="icon-container">
                <PhoneIcon class="contact-icon" />
              </div>
              <span class="contact-text">+86 351 1234 5678</span>
            </div>
            
            <div class="contact-item">
              <div class="icon-container">
                <MailIcon class="contact-icon" />
              </div>
              <span class="contact-text">info@shanxitourism.com</span>
            </div>
          </div>
          
          <!-- 二维码 -->
          <div class="qrcode-container animated-border">
            <div class="qrcode-placeholder">
              <span class="qrcode-text">扫描关注<br>官方微信</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 底部栏 -->
      <div class="footer-bottom">
        <p class="copyright">
          © {{ currentYear }} 山西旅游网. 保留所有权利.
        </p>
        
        <div class="bottom-links">
          <a href="#" class="bottom-link">隐私政策</a>
          <a href="#" class="bottom-link">使用条款</a>
          <a href="#" class="bottom-link">网站地图</a>
        </div>
      </div>
      
      <!-- 返回顶部按钮 -->
      <button 
        @click="scrollToTop" 
        class="back-to-top"
        aria-label="返回顶部"
      >
        <svg class="arrow-icon" viewBox="0 0 24 24">
          <path d="M5 10l7-7m0 0l7 7m-7-7v18"/>
        </svg>
      </button>
    </div>
  </footer>
</template>

<script setup>
import { ref, computed } from 'vue'
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Youtube,
  MapPin,
  Phone,
  Mail
} from 'lucide-vue-next'

// 图标组件重命名
const FacebookIcon = Facebook
const TwitterIcon = Twitter
const InstagramIcon = Instagram
const YoutubeIcon = Youtube
const MapPinIcon = MapPin
const PhoneIcon = Phone
const MailIcon = Mail

// 响应式数据
const socialIcons = ref([FacebookIcon, TwitterIcon, InstagramIcon, YoutubeIcon])

const popularAttractions = ref([
  { id: 1, name: "云冈石窟" },
  { id: 2, name: "平遥古城" },
  { id: 3, name: "五台山" },
  { id: 4, name: "应县木塔" },
  { id: 5, name: "晋祠" }
])

const usefulLinks = ref([
  { label: "旅游攻略", path: "/guides" },
  { label: "交通信息", path: "/transportation" },
  { label: "住宿推荐", path: "/accommodation" },
  { label: "美食指南", path: "/food" },
  { label: "常见问题", path: "/faq" }
])

// 计算属性
const currentYear = computed(() => new Date().getFullYear())

// 方法
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}
</script>

<style scoped>
/* 基础样式 */
.site-footer {
  position: relative;
  background: linear-gradient(to bottom, #8B4513, #6B3100);
  color: white;
  padding: 4rem 0 2rem;
  overflow: hidden;
}

/* 背景装饰 */
.background-decorations {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.mountain-silhouette {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 8rem;
  background: #5A2800;
  opacity: 0.2;
  clip-path: polygon(0% 100%, 15% 65%, 33% 85%, 45% 70%, 60% 80%, 75% 60%, 100% 85%, 100% 100%);
}

.decorative-circle {
  position: absolute;
  border-radius: 50%;
  filter: blur(3rem);
}

.red-blur {
  top: 2.5rem;
  right: 2.5rem;
  width: 16rem;
  height: 16rem;
  background: rgba(195, 39, 43, 0.05);
}

.beige-blur {
  bottom: 2.5rem;
  left: 2.5rem;
  width: 20rem;
  height: 20rem;
  background: rgba(245, 222, 179, 0.05);
}

/* 容器布局 */
.footer-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 1;
}

/* 顶部装饰 */
.top-decoration {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 6rem;
  height: 3rem;
  background: white;
  border-radius: 0 0 3rem 3rem;
  opacity: 0.1;
}

/* 网格布局 */
.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  padding: 2rem 0;
}

/* 品牌区域 */
.brand-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.25rem;
}

.logo {
  width: 2.5rem;
  height: 2.5rem;
  background: white;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
}

.logo-character {
  color: #8B4513;
  font-size: 1.25rem;
  font-family: 'Serif', 'Microsoft YaHei', sans-serif;
}

.brand-title {
  font-size: 1.5rem;
  font-family: 'Serif', 'Microsoft YaHei', sans-serif;
  background: linear-gradient(90deg, #8B4513, #C3272B);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.brand-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 20rem;
}

/* 社交媒体链接 */
.social-links {
  display: flex;
  gap: 0.75rem;
}

.social-link {
  width: 2.25rem;
  height: 2.25rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.social-link:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
}

.social-icon {
  width: 1.125rem;
  height: 1.125rem;
  color: white;
}

/* 通用区块样式 */
.section {
  position: relative;
  padding: 1rem;
}

.section-title {
  font-size: 1.125rem;
  font-family: 'Serif', 'Microsoft YaHei', sans-serif;
  margin-bottom: 1.25rem;
  padding-bottom: 0.5rem;
  position: relative;
  display: inline-block;
}

.title-underline {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 50%;
  height: 2px;
  background: #C3272B;
}

/* 列表项样式 */
.attraction-list,
.useful-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.attraction-item,
.link-item {
  margin: 0.5rem 0;
}

.attraction-link,
.link {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.attraction-link:hover,
.link:hover {
  color: white;
  transform: translateX(0.25rem);
}

.bullet {
  width: 0.375rem;
  height: 0.375rem;
  background: #C3272B;
  border-radius: 50%;
  margin-right: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.attraction-link:hover .bullet,
.link:hover .bullet {
  opacity: 1;
}

/* 联系信息 */
.contact-info {
  display: grid;
  gap: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.icon-container {
  width: 2rem;
  height: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
}

.contact-item:hover .icon-container {
  background: rgba(195, 39, 43, 0.3);
}

.contact-icon {
  width: 1rem;
  height: 1rem;
  color: rgba(255, 255, 255, 0.9);
}

.contact-text {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  transition: color 0.3s ease;
}

.contact-item:hover .contact-text {
  color: white;
}

/* 二维码样式 */
.qrcode-container {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-top: 1.5rem;
  display: inline-block;
}

.qrcode-placeholder {
  width: 5rem;
  height: 5rem;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.qrcode-text {
  color: #8B4513;
  font-size: 0.75rem;
  line-height: 1.2;
}

/* 底部栏 */
.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 1.5rem;
  margin-top: 3rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 1rem;
}

.copyright {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
}

.bottom-links {
  display: flex;
  gap: 1.5rem;
}

.bottom-link {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.3s ease;
}

.bottom-link:hover {
  color: white;
}

/* 返回顶部按钮 */
.back-to-top {
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-to-top:hover {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
}

.arrow-icon {
  width: 1.25rem;
  height: 1.25rem;
  fill: none;
  stroke: white;
  stroke-width: 2;
}

/* 动画边框 */
@keyframes pulse-border {
  0%, 100% {
    border-color: rgba(139, 69, 19, 0.1);
  }
  50% {
    border-color: rgba(139, 69, 19, 0.3);
  }
}

.animated-border {
  border: 1px solid rgba(139, 69, 19, 0.1);
  animation: pulse-border 2s infinite;
}

/* 装饰线 */
.decorative-line.vertical {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 1px;
  background: linear-gradient(to bottom, 
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0) 100%
  );
}

@media (max-width: 768px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .decorative-line.vertical {
    display: none;
  }
  
  .footer-bottom {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
}
</style>